/**
 * @file services/models.ts
 * @description Service for handling models (fulfillments) data for Model Reviewer/Preparer Dashboard
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserFulfillmentRequest } from 'api/models/UserFulfillmentRequest';
import { ApiService } from 'api/services/ApiService';

// Query keys for models operations
export const ModelsQueryKeys = {
  MODELS_LIST: 'models-list',
} as const;

/**
 * Hook to get models list for all companies where user is reviewer/preparer
 * @param filterType - Filter by 'current', 'past', or 'unassigned' models
 * @param preparerUserIds - Optional array of preparer user IDs (not used when filterType is 'unassigned')
 * @param reviewerUserIds - Optional array of reviewer user IDs (not used when filterType is 'unassigned')
 * @param page - Optional page number for pagination - Note: Currently not used by backend
 */
export const useModelsList = (
  filterType: 'current' | 'past' | 'unassigned' | 'all' = 'all',
  preparerUserIds?: number[],
  reviewerUserIds?: number[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  page?: number // Reserved for future pagination support
) => {
  // Create stable string representation of user IDs for dependency comparison
  // Combines both preparer and reviewer IDs into a single stable key
  const userIdsKey = useMemo(() => {
    const preparerIds = preparerUserIds ? [...preparerUserIds].sort((a, b) => a - b) : [];
    const reviewerIds = reviewerUserIds ? [...reviewerUserIds].sort((a, b) => a - b) : [];

    // Create a key that distinguishes between preparer and reviewer IDs
    // Format: "preparer:1,2,3|reviewer:4,5,6" or "preparer:|reviewer:" for empty arrays
    const preparerKey = `preparer:${preparerIds.join(',')}`;
    const reviewerKey = `reviewer:${reviewerIds.join(',')}`;

    return `${preparerKey}|${reviewerKey}`;
  }, [preparerUserIds, reviewerUserIds]);

  const preparerIds = useMemo(
    () => (preparerUserIds ?? []).filter((id): id is number => id !== undefined),
    [preparerUserIds]
  );
  const reviewerIds = useMemo(
    () => (reviewerUserIds ?? []).filter((id): id is number => id !== undefined),
    [reviewerUserIds]
  );
  // Create stable query key - sort and join only once, memoized for performance
  const queryKey = useMemo(() => [ModelsQueryKeys.MODELS_LIST, filterType, userIdsKey], [userIdsKey, filterType]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      // Use the POST API that accepts separate preparer and reviewer IDs
      // When filterType is 'unassigned', use 'unassigned' as status_filter
      const requestBody: UserFulfillmentRequest = {
        preparer_ids: preparerIds,
        reviewer_ids: reviewerIds,
        status_filter: (() => {
          if (filterType === 'all') {
            return UserFulfillmentRequest.status_filter.ALL;
          }
          if (filterType === 'unassigned') {
            return UserFulfillmentRequest.status_filter.UNASSIGNED;
          }
          if (filterType === 'current') {
            return UserFulfillmentRequest.status_filter.CURRENT;
          }
          return UserFulfillmentRequest.status_filter.PAST;
        })(),
      };

      const response = await ApiService.apiDashboardUserModelsCreate(requestBody);

      return response;
    },
    enabled: true, // Always enabled - empty arrays are valid (fetches unassigned models)
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    placeholderData: previousData => previousData, // Keep previous data while loading new data
  });
};
